/**
 * Blog articles for SEO. Each article has a unique slug → /blog/{slug}.
 * Designed to rank on high-intent search queries from danish customers and SMBs.
 *
 * Body uses simple markdown-ish blocks: { h2: "...", p: "..." } or { ul: [...] }.
 */
export const ARTICLES = [
  {
    slug: "hvad-koster-en-kurer-i-2026",
    title: "Hvad koster en kurer i 2026? Komplet prisoversigt for Danmark",
    excerpt:
      "Få det fulde billede af kurérpriser i 2026 — fra 199 kr for en lokal Same-Day levering til faste B2B-aftaler. Se prisformler, eksempler og hvordan du sparer penge på faste ruter.",
    date: "2026-02-17",
    readMinutes: 7,
    category: "Priser",
    image: "/og-default.jpg",
    keywords: ["kurer pris", "hvad koster kurer 2026", "kurérservice pris", "pris kurer Sjælland", "kurer København pris"],
    body: [
      { p: "Kurérpriser i Danmark varierer fra 199 kr for en lokal Same-Day pakke til flere tusinde kroner pr. måned for faste erhvervsaftaler. I denne guide gennemgår vi alle priselementer, så du kan budgettere realistisk for 2026." },
      { h2: "Privatkundens prismodel" },
      { p: "For privatkunder er prisen typisk opbygget af en startpris, en kilometer-baseret andel, og eventuelle tillæg for weekend eller aftentid. EMA Kurer ApS bruger denne enkle formel:" },
      { ul: [
        "Startpris 199 kr (Same-Day) inkluderer de første 8 km",
        "Hver ekstra km koster 6 kr",
        "Weekend-tillæg: +20% (lørdag/søndag)",
        "Sen-aften-tillæg: +15% (afhentning efter kl. 20)",
      ]},
      { h2: "Eksempler på faktiske priser" },
      { ul: [
        "Inden for København (5 km): 199 kr",
        "København → Roskilde (30 km): 199 + 22×6 = 331 kr",
        "København → Næstved (85 km, hverdag): 199 + 77×6 = 661 kr",
        "København → Roskilde lørdag aften: 331 × 1,2 × 1,15 = 457 kr",
      ]},
      { h2: "Express, Aftenlevering og Erhverv" },
      { p: "Express (under 90 min) starter fra 289 kr. Aftenlevering 18-22 koster fra 249 kr. Erhvervskunder med faste ruter får priser fra 1.999 kr/md (Starter-pakke 1 daglig rute) op til 8.999 kr/md (Premium med 5 daglige ruter + dedikeret SLA)." },
      { h2: "Hvad afgør prisen?" },
      { ul: [
        "Distance (kilometer fra A til B)",
        "Hastighed (Same-Day, Express, Aften)",
        "Tidspunkt (weekend/aften = tillæg)",
        "Volumen (flere pakker = bedre stykpris)",
        "Frekvens (faste ruter = abonnementsrabat)",
      ]},
      { h2: "Sådan sparer du på kurerudgifter" },
      { p: "Hvis du sender mere end 20 pakker pr. måned, er en fast B2B-aftale stort set altid billigere end stykpris. En typisk webshop med 60-80 pakker pr. måned betaler 4.499 kr/md (Business-pakke) i stedet for ca. 12.000-16.000 kr på stykpris. Du får også prioriteret afhentning, dedikeret kontaktperson og månedsfakturering." },
      { h2: "Konklusion" },
      { p: "En kurer i 2026 koster fra 199 kr for en simpel lokal levering. Sammenlign altid den fulde formel (start + km + tillæg) — ikke kun startprisen — og overvej en B2B-aftale hvis du sender regelmæssigt." },
    ],
  },
  {
    slug: "same-day-vs-express-kurer-hvad-skal-du-vaelge",
    title: "Same-Day vs Express kurer — hvad skal du vælge?",
    excerpt:
      "Same-Day eller Express? Vi forklarer forskellen, prissætningen og hvornår du bør vælge hvilken type kurérservice i 2026.",
    date: "2026-02-17",
    readMinutes: 5,
    category: "Guides",
    image: "/og-default.jpg",
    keywords: ["same-day kurer", "express kurer", "hurtig pakkelevering", "kurer service forskelle"],
    body: [
      { p: "De to mest brugte kurértyper i Danmark er Same-Day og Express. Forskellen lyder enkel, men har stor betydning for både pris og leveringstid. Vi forklarer hvornår du skal bruge hvilken." },
      { h2: "Same-Day kurer — pakken kommer i dag" },
      { p: "Same-Day betyder, at pakken hentes og leveres samme dag — typisk inden for 3-6 timer. Det er ideel til ikke-akutte men dag-til-dag leverancer: reservedele, kontrakter, prøvematerialer. Pris: fra 199 kr inkl. 8 km." },
      { h2: "Express kurer — afsted med det samme" },
      { p: "Express betyder typisk under 90 minutter fra booking til levering. Chaufføren er på vej inden for få minutter. Det er den rigtige løsning når noget er tidskritisk: nøgler, lægedokumenter, akutreparationer. Pris: fra 289 kr inkl. 8 km." },
      { h2: "Hvornår skal du vælge hvad?" },
      { ul: [
        "Vælg Same-Day hvis du booker om morgenen og vil have leveret samme dag",
        "Vælg Express hvis det skal være fremme inden for 1-2 timer",
        "Vælg Aftenlevering hvis modtageren først er hjemme efter kl. 17",
        "Vælg Erhvervskurér hvis du har 5+ leverancer/dag på faste tider",
      ]},
      { h2: "Real-life eksempel" },
      { p: "En tandlæge i København har glemt en specifik tand-skinne på klinikken. Patienten er stadig i stolen. Express er det rigtige valg — afsted nu, 289 kr, fremme inden patienten skal hjem. En webshop, der sælger gaver, vælger derimod Same-Day kl. 12 og betaler 199-241 kr for at få ordren ud før aften." },
    ],
  },
  {
    slug: "qr-kode-pakkelevering-saadan-virker-det",
    title: "QR-kode pakkelevering — sådan virker det",
    excerpt:
      "Hver pakke får en unik QR-kode der scannes ved afhentning. Vi forklarer fordelene, sikkerheden og hvordan det føles for dig som kunde.",
    date: "2026-02-17",
    readMinutes: 4,
    category: "Teknologi",
    image: "/og-default.jpg",
    keywords: ["QR-kode pakke", "QR-kode kurer", "pakkesporing QR", "kontaktløs levering"],
    body: [
      { p: "Vi sender en unik QR-kode med din bookingbekræftelse. Chaufføren scanner koden ved afhentning, og pakken aktiveres automatisk i systemet. Det er moderne, sikkert og uden papirarbejde." },
      { h2: "Hvorfor QR-kode?" },
      { ul: [
        "100% korrekt match mellem ordre og chauffør — ingen forvekslinger",
        "Automatisk timestamp på afhentning (relevant for forsikringsformål)",
        "Du behøver ikke huske bookingnummer eller printe noget",
        "Live tracking aktiveres automatisk ved første scan",
        "GDPR-venlig: ingen synlig persondata på koden",
      ]},
      { h2: "Sådan bruger du QR-koden" },
      { p: "Når du har booket og betalt, modtager du en e-mail med både PDF-faktura og QR-kode. Du kan også åbne koden på siden /track ved at indtaste bookingnummer + email. Vis blot koden på telefonen, når chaufføren kommer." },
      { h2: "Sikkerhed" },
      { p: "QR-koden er en 32-tegns hex-token, som kun kan bruges én gang og kun før levering. Når pakken er leveret, returnerer endpoint en 410 Gone-fejl — koden kan ikke genbruges af nogen anden." },
    ],
  },
  {
    slug: "saadan-vaelger-du-den-rigtige-kurer-til-din-webshop",
    title: "Sådan vælger du den rigtige kurer til din webshop",
    excerpt:
      "8 punkter at vurdere når du vælger kurer til din webshop — fra prismodel og dækning til SLA og integrationsmuligheder.",
    date: "2026-02-17",
    readMinutes: 6,
    category: "B2B",
    image: "/og-default.jpg",
    keywords: ["kurer webshop", "vælg kurer", "logistik webshop", "leveringsservice webshop"],
    body: [
      { p: "Den kurer du vælger har enorm betydning for kundeoplevelsen i din webshop. Her er 8 kriterier vi anbefaler du vurderer alle dine leverandører ud fra." },
      { h2: "1. Direkte A → B eller hub-baseret?" },
      { p: "Almindelige pakkefirmaer (PostNord, GLS, DAO) bruger hub-and-spoke: pakken kører til et lager, sorteres og kører ud igen. En direkte kurer (som EMA Kurer) henter hos dig og kører lige til kunden — uden mellemstop." },
      { h2: "2. Leveringstidspunkt" },
      { p: "Almindelig pakkelevering tager 1-3 dage. Same-Day kurer leverer samme dag. Express på under 90 minutter. Match valget til kundeforventningen — premium-produkter fortjener premium-levering." },
      { h2: "3. Prismodel" },
      { ul: [
        "Stykpris: du betaler pr. levering — fleksibelt men dyrt ved volumen",
        "Abonnement: fast månedspris med inkluderet volumen — billigt ved >20 ordrer/md",
        "Hybrid: månedlig grundpris + variabelt km-tillæg",
      ]},
      { h2: "4. Geografisk dækning" },
      { p: "Tjek om kurerens kerneområde matcher dine kunder. En Sjælland-dækkende kurer er ideel hvis 80% af dine ordrer går til Storkøbenhavn. Til nationale ordrer kan du kombinere flere kurerer." },
      { h2: "5. Tracking og kommunikation" },
      { p: "Kunderne skal kunne se hvor pakken er. Tjek om kureren tilbyder live GPS, SMS, e-mail-notifikationer ved hver milepæl, og foto-bevis ved levering." },
      { h2: "6. SLA og garantier" },
      { p: "Spørg om gennemsnitlig leveringstid, antal forsøg ved 'ikke hjemme', og hvad der sker hvis pakken bliver væk. Få det skriftligt." },
      { h2: "7. Integrationsmuligheder" },
      { p: "Kan kureren modtage ordrer fra dit shopsystem via API eller CSV-upload? Skal du selv oprette hver ordre manuelt? For webshops med 10+ ordrer/dag er API integration et must." },
      { h2: "8. Månedsfakturering" },
      { p: "Erhvervskunder bør have månedlig samlefaktura — ikke kvittering pr. levering. Det letter bogføringen og giver bedre overblik." },
      { h2: "Bonus: Spørg om de bruger underleverandører" },
      { p: "Mange store kurérfirmaer outsourcer til vognmænd uden direkte kontrol. Det giver ofte dårligere kvalitet. Vælg en kurer der enten kører selv eller har tæt styring med deres vognmænd (KPI'er, performance dashboard, audit-trail)." },
    ],
  },
  {
    slug: "fast-kureraftale-naar-betaler-sig",
    title: "Fast kuréraftale — hvornår betaler det sig?",
    excerpt:
      "Vi regner det ud: hvornår skifter du fra stykpris til abonnement, og hvor meget kan du spare? Se beregningseksempler og break-even-analyser.",
    date: "2026-02-17",
    readMinutes: 5,
    category: "B2B",
    image: "/og-default.jpg",
    keywords: ["fast kuréraftale", "kurer abonnement", "B2B kurer pris", "månedlig kurer"],
    body: [
      { p: "Hvis du sender pakker med en kurer ugentligt, så lønner det sig næsten altid at skifte til en fast aftale. Vi viser dig de tre realistiske scenarier." },
      { h2: "Stykpris vs abonnement — den simple regnemodel" },
      { p: "Ved EMA Kurer er Starter-pakken (1 daglig rute) 1.999 kr/md. Det svarer til ca. 100 kr pr. levering hvis du har 20 leveringer/md — vs 199-289 kr pr. levering på stykpris." },
      { h2: "Scenarie 1: Den lille webshop" },
      { p: "Du sender 30 ordrer/md inden for Storkøbenhavn. Stykpris: 30 × 220 = 6.600 kr. Abonnement (Business 4.499 kr/md): 4.499 kr — spar 2.100 kr/md (32%)." },
      { h2: "Scenarie 2: Apotek/klinik med faste daglige ruter" },
      { p: "Du har 5 daglige leverancer 22 dage/md = 110 leverancer. Stykpris: 110 × 235 = 25.850 kr. Premium-aftale (8.999 kr/md): spar 16.851 kr/md (65%)." },
      { h2: "Scenarie 3: Den lille håndværker" },
      { p: "10 leveringer/md sporadisk. Stykpris: 10 × 215 = 2.150 kr. Starter (1.999 kr/md inkluderer 20/md) — spar 151 kr men får 10 ekstra leveringer gratis i puljen. Brug puljen til at sende mere → marketing-fordel." },
      { h2: "Hvad får du udover prisrabat?" },
      { ul: [
        "Dedikeret kontaktperson (én person at ringe til)",
        "Månedsfaktura via Billy.dk / e-conomic — én linje at bogføre",
        "Garanteret SLA — du står først i køen",
        "API/CSV bulk-upload af bookings",
        "Mulighed for branding (kurérens uniformer, kasser)",
      ]},
      { p: "Bemærk: priserne gælder inden for det aftalte leveringsområde og afstand. Langture aftales særskilt." },
    ],
  },
  {
    slug: "kurer-sjaelland-saadan-fungerer-det",
    title: "Kurer på Sjælland — sådan fungerer det",
    excerpt:
      "Vi dækker hele Sjælland med direkte kurérservice. Få overblik over zoner, ruter, priser og leveringstider på Sjælland.",
    date: "2026-02-17",
    readMinutes: 5,
    category: "Sjælland",
    image: "/og-default.jpg",
    keywords: ["kurer Sjælland", "kurérservice Sjælland", "pakke Sjælland", "lokalkurer Sjælland"],
    body: [
      { p: "EMA Kurer dækker hele Sjælland — fra København i øst til Kalundborg i vest, fra Helsingør i nord til Falster/Lolland i syd. Vores fokus er direkte A → B, uden hub eller mellemstop." },
      { h2: "Dækkede byer" },
      { ul: [
        "København + omegn (alle postnumre 1000–2900)",
        "Roskilde, Køge, Greve, Solrød",
        "Helsingør, Hillerød, Frederikssund, Lyngby",
        "Næstved, Slagelse, Holbæk, Kalundborg",
        "Hele Vestsjælland og Sydsjælland",
      ]},
      { h2: "Typiske ruter og priser" },
      { ul: [
        "København → Roskilde (30 km): 199 + 22×6 = 331 kr",
        "København → Helsingør (45 km): 199 + 37×6 = 421 kr",
        "København → Næstved (85 km): 199 + 77×6 = 661 kr",
        "Roskilde → Køge (28 km): 199 + 20×6 = 319 kr",
      ]},
      { h2: "Hvor hurtigt kan vi være fremme?" },
      { p: "Inden for Storkøbenhavn typisk 60-90 minutter ved Same-Day. Til Roskilde/Køge ca. 90 min. Til Næstved/Holbæk ca. 2 timer. Express er ca. halvdelen af tiden." },
      { h2: "Hvad gør os anderledes på Sjælland?" },
      { p: "I modsætning til de store pakkefirmaer henter vi pakken hos dig og kører lige til modtageren — ingen pakkeshop, ingen overnatning på lager, ingen omlastning. Du sparer tid, og pakken bliver behandlet professionelt fra start til slut." },
    ],
  },
  {
    slug: "leveringstid-paa-pakker-saadan-faar-du-pakken-hurtigere",
    title: "Leveringstid på pakker — sådan får du pakken hurtigere",
    excerpt:
      "8 praktiske tips til at reducere leveringstiden på dine pakker — uanset om du er privatkunde eller webshop-ejer.",
    date: "2026-02-17",
    readMinutes: 5,
    category: "Tips",
    image: "/og-default.jpg",
    keywords: ["hurtig levering", "leveringstid pakke", "speed up delivery", "kurer leveringstid"],
    body: [
      { p: "Vil du have pakken hurtigt frem? Her er 8 praktiske tips der virker — uanset om du sender med kurer eller almindelig pakkeservice." },
      { h2: "1. Book før kl. 12" },
      { p: "Same-Day med booking før middag har næsten altid levering inden for samme arbejdsdag. Senere bookings rykkes ofte til næste dag — også selvom det ikke står direkte i bekræftelsen." },
      { h2: "2. Vælg Express ved tidskritiske leverancer" },
      { p: "Express er typisk 50% hurtigere end Same-Day, fordi chaufføren rutes direkte uden andre stop." },
      { h2: "3. Sørg for at modtageren er hjemme" },
      { p: "Ring/SMS modtageren før chaufføren ankommer. 'Ikke hjemme' er den hyppigste årsag til genleveringer — og forsinkelser på 24-48 timer." },
      { h2: "4. Brug en lokal kurer i stedet for nationalt pakkefirma" },
      { p: "Direkte A → B er altid hurtigere end hub-baseret levering. En lokal kurer på Sjælland leverer Storkøbenhavn → Roskilde på 90 min. PostNord/GLS tager typisk 1-2 dage." },
      { h2: "5. Tjek pakkens dimensioner og vægt" },
      { p: "Pakker over 11 kg, over 60×60×60 cm eller med ikke-standardform kan kræve specialudstyr og forsinke leveringen. Mål altid før booking." },
      { h2: "6. Inkluder klare leveringsinstruktioner" },
      { p: "'Ring på dørtelefonen' eller 'Aflever til nabo 4B' kan spare 10-15 minutter pr. levering. Felt 'Fuldmagt'/leveringsnoter findes på de fleste platforme." },
      { h2: "7. Brug aftenlevering ved erhvervsadresser" },
      { p: "Mange virksomheder lukker kl. 16. Aftenlevering 18-22 sikrer at private modtagere er hjemme — uden tomgangs-ture." },
      { h2: "8. Aftal genleveringspolitik på forhånd" },
      { p: "Hvis modtageren ikke er hjemme — skal pakken så afleveres til nabo, retur til afsender, eller forsøges igen? Aftal det i bookingen, ikke når pakken allerede er ude på vejen." },
    ],
  },
  {
    slug: "saadan-bliver-du-vognmand-for-en-kurer",
    title: "Sådan bliver du vognmand (underleverandør) for en kurer",
    excerpt:
      "Drømmer du om at starte som vognmand med kurerkørsel? Vi gennemgår krav, kontrakter, indtjening og hvordan du tilmelder dig EMA Kurer.",
    date: "2026-02-17",
    readMinutes: 6,
    category: "Karriere",
    image: "/og-default.jpg",
    keywords: ["bliv vognmand", "kurer vognmand", "freelance kurer", "kurer underleverandør"],
    body: [
      { p: "Som vognmand kører du for en kurérplatform med din egen bil/varevogn. Du får mere fleksibilitet end som ansat chauffør og kan opbygge en lille flåde. Her er vejen til at komme i gang." },
      { h2: "Krav til at starte" },
      { ul: [
        "Eget CVR-nummer (kan oprettes gratis på virk.dk)",
        "Egen bil/varevogn (max 3.500 kg) — eller flere",
        "Erhvervsforsikring (ansvars + last)",
        "Renset straffeattest for alle chauffører",
        "Gyldigt B-kørekort (minimum)",
      ]},
      { h2: "Sådan tilmelder du dig EMA Kurer" },
      { p: "Vi onboarder vognmænd løbende. Du opretter dine chauffører i vores vognmandsportal, uploader dokumenter (straffeattest, kørekort, ansættelseskontrakt), og venter på godkendelse fra EMA Kurer admin. Når du er aktiv, modtager du opgaver direkte i appen." },
      { h2: "Indtjening" },
      { p: "Som vognmand for EMA Kurer faktureres dine leveringer månedligt med netto 14 dages betaling. Du modtager en detaljeret Excel-opgørelse med alle ture, kilometer og beløb pr. chauffør. Bøder for service-brud trækkes automatisk i opgørelsen." },
      { h2: "Performance tracker" },
      { p: "Vi har en transparent KPI-model: on-time rate (50%), fail-rate (30%) og call-compliance (20%). Du kan altid se din egen og dine chaufførers performance i vognmandsportalen. Højt scorende vognmænd får flere ture." },
      { h2: "Krav til løbende drift" },
      { ul: [
        "Min. 95% on-time leveringer",
        "Maks. 5% forgæves leveringer",
        "Obligatorisk kundekontakt før 'recipient_not_home' kan registreres",
        "Følg vores GDPR + uniformspolitik",
      ]},
      { h2: "Næste skridt" },
      { p: "Send en ansøgning via /bliv-chauffoer eller skriv direkte til kontakt@emakurer.dk. Vi vender tilbage inden for 48 timer." },
    ],
  },
  {
    slug: "moms-paa-kurerservice-saadan-fungerer-det",
    title: "Moms på kurérservice — sådan fungerer det",
    excerpt:
      "Skal du beregne moms på dine kurérudgifter? Hvornår kan du trække den fra? Vi gennemgår momsreglerne for B2B og privatkunder.",
    date: "2026-02-17",
    readMinutes: 4,
    category: "Bogføring",
    image: "/og-default.jpg",
    keywords: ["moms kurer", "moms kurérservice", "kurer fradrag moms", "B2B kurer moms"],
    body: [
      { p: "Kurérservice er en momspligtig ydelse i Danmark — alle priser tillægges 25% moms. Her er hvad du skal vide om fradrag og fakturering." },
      { h2: "Alle priser er inkl. moms — som standard" },
      { p: "På emakurer.dk vises alle priser inklusive 25% moms. Det betyder at en Same-Day-levering på 199 kr består af 159,20 kr ekskl. moms + 39,80 kr moms." },
      { h2: "B2B med CVR — fuldt fradragsberettiget" },
      { p: "Hvis du har CVR og er momsregistreret, kan du trække hele momsen fra som indgående moms. På din faktura er kurerudgiften en omkostning der reducerer din skattepligtige indtjening 1:1." },
      { h2: "Hvad skal stå på fakturaen?" },
      { ul: [
        "EMA Kurer ApS' CVR-nummer (46465717)",
        "Dato for ydelsen",
        "Beskrivelse af leveringen (start/slut, dato)",
        "Beløb ekskl. moms",
        "Moms 25%",
        "Total inkl. moms",
        "Dit firmanavn + CVR (hvis B2B)",
      ]},
      { h2: "Privatpersoner" },
      { p: "Privatpersoner kan ikke trække moms fra. Prisen du betaler er det endelige beløb." },
      { h2: "EAN/OIOUBL til offentlige kunder" },
      { p: "Statslige og kommunale kunder kan modtage faktura via EAN-nummer i OIOUBL-format. Vi understøtter dette — kontakt bogholderi@emakurer.dk for setup." },
    ],
  },
  {
    slug: "saadan-pakker-du-din-pakke-til-kurer",
    title: "Sådan pakker du din pakke korrekt til kurer",
    excerpt:
      "Praktisk guide til pakning: emballage, polstring, mærkning, vægt og volumen — så pakken kommer sikkert frem.",
    date: "2026-02-17",
    readMinutes: 5,
    category: "Tips",
    image: "/og-default.jpg",
    keywords: ["pak pakke", "pakke emballage", "sikker forsendelse", "pakning til kurer"],
    body: [
      { p: "En velpakket pakke ankommer sikkert frem. En dårligt pakket pakke kan ødelægges undervejs — også selvom kureren håndterer den professionelt. Her er vores 7-trins guide." },
      { h2: "1. Vælg den rigtige emballage" },
      { p: "Brug helst en stiv papkasse — to-lags bølgepap er optimalt til alt over 1 kg. Genbrugte kasser virker, men tjek at flapper ikke er blødgjorte." },
      { h2: "2. Polstring indvendigt" },
      { p: "Sørg for at indholdet ikke kan flytte sig. Brug bobleplast, papir-fill eller skum. En tom hjørne i kassen = en svaghed der kan kollapse under tryk." },
      { h2: "3. Tape — krydsmærket" },
      { p: "Brug bred pakketape og luk i 'H-form': én stribe ned langs midten + to på tværs ved hver kant. Tjek alle hjørner." },
      { h2: "4. Mærkning udvendigt" },
      { ul: [
        "Modtagerens navn + fulde adresse (gade, nr., evt. lejlighed)",
        "Telefonnummer på modtager",
        "Afsenderens info i mindre skrift",
        "Eventuelle 'forsigtig'/'denne side opad' labels",
      ]},
      { h2: "5. Vægt og dimensioner" },
      { p: "Max-vægt for EMA Kurer er 11 kg pr. pakke. Max mål: 60×60×60 cm. Større/tungere pakker kræver særskilt aftale." },
      { h2: "6. Skrøbelige varer" },
      { p: "Marker tydeligt 'SKRØBELIGT' med rødt. Tilføj 'denne side opad'-pile. Skrøbelige varer skal polstres med min. 5 cm beskyttelse på alle sider." },
      { h2: "7. Fuldmagt-felt — brug det" },
      { p: "Hvis pakken må afleveres til nabo eller hænges på dørhåndtaget, skriv det i 'fuldmagt'-feltet ved booking. Det sparer en genlevering hvis modtageren ikke er hjemme." },
    ],
  },
];

/** Helper: find by slug */
export const findArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
